/**
 * API Service Utility for FollowUs
 * Handles all API calls to the Django REST Framework backend
 */

// Base API URL from env (e.g. https://follow-us.crowdnex.fr). In dev, use empty for proxy.
const _RAW_API_BASE = (typeof process !== 'undefined' && process.env.NODE_ENV === 'development')
  ? ''
  : (process.env.REACT_APP_API_URL || 'http://localhost:8000');

/**
 * Get API base URL with tenant subdomain injected at runtime.
 * Format: https://{tenant}.yourdomain.com when tenant is available.
 * @param {boolean} [includeTenant=true] - If false, use platform base (e.g. for /api/public/tenants/)
 * @returns {string} Base URL for API requests (no trailing slash)
 */
function getApiBaseUrl(includeTenant = true) {
  if (!_RAW_API_BASE) return ''; // Dev: relative URLs via proxy
  try {
    const baseUrl = _RAW_API_BASE.replace(/\/+$/, '');
    const url = new URL(baseUrl);
    const host = url.hostname || '';
    const protocol = url.protocol || 'https:';
    const port = url.port ? `:${url.port}` : '';
    if (!includeTenant) return `${protocol}//${host}${port}`;
    const tenant = typeof window !== 'undefined' ? getTenantSubdomain() : null;
    if (!tenant) return `${protocol}//${host}${port}`;
    // Inject tenant as subdomain: https://{tenant}.follow-us.crowdnex.fr
    const tenantHost = `${tenant}.${host}`;
    return `${protocol}//${tenantHost}${port}`;
  } catch (e) {
    return _RAW_API_BASE.replace(/\/+$/, '');
  }
}

/** Backend origin for media URLs (tenant-injected at runtime). */
function getBackendBaseUrl() {
  if (!_RAW_API_BASE) return '';
  try {
    const base = getApiBaseUrl(true);
    return base ? new URL(base).origin : String(_RAW_API_BASE).replace(/\/+$/, '');
  } catch (e) {
    return String(_RAW_API_BASE || '').replace(/\/+$/, '');
  }
}
const TOKEN_KEY_PREFIX = 'followus';
/** Get tenant-scoped storage keys to enforce tenant isolation (tokens from tenant A cannot be used for tenant B). */
function getTokenKeys() {
  const tenant = typeof window !== 'undefined' ? getTenantSubdomain() : null;
  const suffix = tenant ? `_tenant_${tenant}` : '';
  return {
    ACCESS: `${TOKEN_KEY_PREFIX}_access_token${suffix}`,
    REFRESH: `${TOKEN_KEY_PREFIX}_refresh_token${suffix}`,
    MUST_CHANGE_PASSWORD: `${TOKEN_KEY_PREFIX}_must_change_password${suffix}`,
  };
}

/** Default time to wait for API response before aborting (ms). Use REACT_APP_API_TIMEOUT_MS to override. */
const API_REQUEST_TIMEOUT_MS = Number(process.env.REACT_APP_API_TIMEOUT_MS) || 300000;

/** Longer timeout for heavy/filtered requests (dashboard stats, imputations, lists with filters) so they don't time out. */
const HEAVY_REQUEST_TIMEOUT_MS = Number(process.env.REACT_APP_HEAVY_REQUEST_TIMEOUT_MS) || 900000;

/** Error message shown only for real network/connection failures (not for timeout or slow response). */
const CONNECTION_ERROR_MESSAGE = 'Unable to connect to server. Please check your connection.';

/** Error message when the request is aborted after exceeding API_REQUEST_TIMEOUT_MS. */
const TIMEOUT_ERROR_MESSAGE = 'Request timed out. The server is taking too long to respond.';

/** Error message when connection dropped after receiving response headers (e.g. during body read). */
const BODY_READ_ERROR_MESSAGE = 'Connection lost while receiving response. Please try again.';

/** Retry delay (ms) for transient NETWORK/BODY_READ_ERROR before retry. */
const RETRY_DELAY_MS = 2500;

/** Max attempts (initial + retries) for transient failures. */
const RETRY_MAX_ATTEMPTS = 2;

/** Safe subdomain pattern (alphanumeric and hyphens, same as backend). */
const TENANT_SUBDOMAIN_REGEX = /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/;

/**
 * When using the dev proxy (relative URLs), the proxy forwards to localhost:8000 and does not
 * forward the X-Tenant header, so the backend never sees the tenant. Appending tenant as a query
 * parameter ensures Django receives it via request.GET.get('tenant').
 * @param {string} url - URL (relative or absolute)
 * @param {string|null} tenant - Tenant subdomain
 * @returns {string} URL with ?tenant= or &tenant= appended when in dev and tenant is set
 */
function ensureTenantQueryParam(url, tenant) {
  if (!tenant || !TENANT_SUBDOMAIN_REGEX.test(tenant)) return url;
  if (!_RAW_API_BASE) {
    const sep = url.includes('?') ? '&' : '?';
    return url + sep + 'tenant=' + encodeURIComponent(tenant);
  }
  return url;
}

/** Production base domain; anything before it is the tenant subdomain. Default: follow-us.crowdnex.fr */
const PRODUCTION_BASE_DOMAIN = (typeof process !== 'undefined' && process.env.REACT_APP_BASE_DOMAIN)
  ? String(process.env.REACT_APP_BASE_DOMAIN).trim().toLowerCase()
  : 'follow-us.crowdnex.fr';

/** Build tenant URL for production. Uses hostname base (e.g. rs.follow-us.crowdnex.fr -> follow-us.crowdnex.fr). */
export function getTenantUrl(subdomain) {
  if (typeof window === 'undefined' || !subdomain) return '#';
  const host = (window.location.hostname || '').toLowerCase();
  if (['localhost', '127.0.0.1', '::1'].includes(host)) return `?tenant=${subdomain}`;
  const parts = host.split('.');
  const base = parts.length > 3 ? parts.slice(1).join('.') : (host || PRODUCTION_BASE_DOMAIN);
  const protocol = window.location.protocol || 'https:';
  return `${protocol}//${subdomain}.${base}${window.location.port ? ':' + window.location.port : ''}`;
}

/**
 * Get current tenant subdomain for multi-tenant API requests.
 * - From hostname: company1.localhost or company1.127.0.0.1 -> 'company1'
 * - From hostname: company1.follow-us.crowdnex.fr -> 'company1' (prod)
 * - From URL query: ?tenant=company1 (when on localhost/127.0.0.1 or base domain)
 * - From env: REACT_APP_DEFAULT_TENANT (e.g. for local dev when not using subdomain)
 * @returns {string|null} Tenant subdomain or null
 */
export function getTenantSubdomain() {
  if (typeof window !== 'undefined' && window.location) {
    const hostname = (window.location.hostname || '').toLowerCase();
    const search = window.location.search || '';
    // Subdomain from hostname (e.g. company1.localhost, company1.127.0.0.1)
    if (hostname.endsWith('.localhost') || hostname.endsWith('.127.0.0.1')) {
      const part = hostname.split('.')[0];
      if (part && TENANT_SUBDOMAIN_REGEX.test(part)) return part;
    }
    // Production: tenant.follow-us.crowdnex.fr -> tenant (base domain is follow-us.crowdnex.fr)
    if (hostname.endsWith('.' + PRODUCTION_BASE_DOMAIN) && hostname.length > PRODUCTION_BASE_DOMAIN.length) {
      const prefix = hostname.slice(0, -(PRODUCTION_BASE_DOMAIN.length + 1));
      if (prefix && TENANT_SUBDOMAIN_REGEX.test(prefix)) return prefix;
    }
    // Query param (e.g. localhost:3000?tenant=company1 or follow-us.crowdnex.fr?tenant=company1)
    const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '::1';
    const isBaseDomain = hostname === PRODUCTION_BASE_DOMAIN;
    if (isLocalhost || isBaseDomain) {
      const params = new URLSearchParams(search);
      const tenantFromParams = (params.get('tenant') || '').trim().toLowerCase();
      if (tenantFromParams && TENANT_SUBDOMAIN_REGEX.test(tenantFromParams)) return tenantFromParams;
    }
  }
  // Fallback: default tenant from env (e.g. REACT_APP_DEFAULT_TENANT=company1 for local dev)
  const defaultTenant = typeof process !== 'undefined' && process.env.REACT_APP_DEFAULT_TENANT;
  const trimmed = (defaultTenant && String(defaultTenant).trim().toLowerCase()) || null;
  if (trimmed && TENANT_SUBDOMAIN_REGEX.test(trimmed)) return trimmed;
  return null;
}

/**
 * Get tenant status from backend: { subdomain, exists, reason, error }.
 * reason: 'ok' | 'inactive_or_archived' | 'not_found'
 */
export async function getTenantStatus() {
  const tenantFromUrl = getTenantSubdomain();
  const base = getApiBaseUrl(!!tenantFromUrl);
  let fullUrl = `${base || ''}/api/health_1/api/v1/`;
  fullUrl = ensureTenantQueryParam(fullUrl, tenantFromUrl);
  const headers = {};
  if (tenantFromUrl) headers['X-Tenant'] = tenantFromUrl;
  try {
    const res = await fetch(fullUrl, { method: 'GET', headers });
    const data = await res.json().catch(() => ({}));
    if (res.status === 200) return { subdomain: data.tenant ?? tenantFromUrl, exists: true, reason: 'ok' };
    if (res.status === 404 && data.code === 'tenant_not_found') {
      return {
        subdomain: data.subdomain ?? tenantFromUrl ?? null,
        exists: false,
        reason: data.reason ?? 'not_found',
        error: data.error,
      };
    }
    return { subdomain: tenantFromUrl, exists: false, reason: 'not_found' };
  } catch {
    return { subdomain: tenantFromUrl, exists: false, reason: 'not_found' };
  }
}

/**
 * Get the current tenant as resolved by the backend (for debugging).
 * Calls the health endpoint; backend returns tenant in 200 response or subdomain in 404 body.
 * @returns {Promise<string|null>} Tenant subdomain from backend, or null
 */
export async function getTenantFromBackend() {
  const status = await getTenantStatus();
  return status.subdomain ?? null;
}

/**
 * Get list of available tenants from backend (subdomain + name).
 * Call without X-Tenant so request hits platform host.
 * @returns {Promise<Array<{subdomain: string, name: string}>>}
 */
export async function getAvailableTenants() {
  const fullUrl = `${getApiBaseUrl(false)}/api/public/tenants/`;
  try {
    const res = await fetch(fullUrl, { method: 'GET' });
    if (!res.ok) return [];
    const data = await res.json().catch(() => ({}));
    return Array.isArray(data.tenants) ? data.tenants : [];
  } catch {
    return [];
  }
}

/**
 * Check if the current tenant (from subdomain / ?tenant= / env) exists in the backend DB.
 * Calls a lightweight API; backend returns 404 with code "tenant_not_found" when tenant is missing.
 * @returns {Promise<boolean>} true if tenant exists and backend returned 200, false otherwise
 */
export async function checkTenantExists() {
  const tenant = getTenantSubdomain();
  if (!tenant) return false;
  let fullUrl = `${getApiBaseUrl(true)}/api/health_1/api/v1/`;
  fullUrl = ensureTenantQueryParam(fullUrl, tenant);
  const headers = { 'X-Tenant': tenant };
  try {
    const res = await fetch(fullUrl, { method: 'GET', headers });
    const data = await res.json().catch(() => ({}));
    if (res.status === 200) return true;
    if (res.status === 404) {
      if (data.code === 'tenant_not_found') return false;
    }
    return false;
  } catch {
    return false;
  }
}

/**
 * Detect abort/timeout errors reliably across browsers and polyfills.
 * AbortController.abort() rejects with AbortError (DOMException) or equivalent.
 */
function isAbortError(error) {
  if (!error) return false;
  if (error.name === 'AbortError') return true;
  if (typeof DOMException !== 'undefined' && error instanceof DOMException && error.code === 20) return true;
  const msg = error.message && String(error.message).toLowerCase();
  return !!(msg && (msg.includes('abort') || msg.includes('aborted')));
}

/**
 * Safely join a base URL and a relative path.
 * - Handles missing/extra slashes.
 * - Leaves absolute URLs unchanged.
 */
function toAbsoluteUrl(baseUrl, maybeRelativePath) {
  if (!maybeRelativePath) return null;
  if (typeof maybeRelativePath !== 'string') return String(maybeRelativePath);

  const value = maybeRelativePath.trim();
  if (!value) return null;

  // If already a full URL, return as-is
  if (value.startsWith('http://') || value.startsWith('https://')) {
    return value;
  }

  const base = String(baseUrl || '').replace(/\/+$/, '');
  const path = value.startsWith('/') ? value : `/${value}`;
  return `${base}${path}`;
}

/**
 * Get full URL for profile picture
 * @param {string|null} profilePicturePath - Relative path from backend (e.g., /media/profile_pictures/...)
 * @returns {string|null} Full URL or null if no picture
 */
export function getProfilePictureUrl(profilePicturePath) {
  return toAbsoluteUrl(getBackendBaseUrl(), profilePicturePath);
}

/**
 * Get full URL for badge icon
 * @param {string|null} badgeIconPath - Relative path from backend (e.g., /media/badge_icons/...)
 * @returns {string|null} Full URL or null if no icon
 */
export function getBadgeIconUrl(badgeIconPath) {
  return toAbsoluteUrl(getBackendBaseUrl(), badgeIconPath);
}

/**
 * Generic API call handler
 * @param {string} url - API endpoint (relative to base URL)
 * @param {string} method - HTTP method (GET, POST, PUT, DELETE)
 * @param {object} data - Request body data
 * @param {boolean} requiresAuth - Whether to include auth token
 * @param {object} [callOptions] - Optional: { timeoutMs } for longer timeout (e.g. heavy date-range requests)
 * @returns {Promise} API response
 */
async function apiCall(url, method = 'GET', data = null, requiresAuth = false, callOptions = {}) {
  const timeoutMs = callOptions.timeoutMs != null ? callOptions.timeoutMs : API_REQUEST_TIMEOUT_MS;

  let fullUrl = `${getApiBaseUrl(true)}${url}`;
  fullUrl = ensureTenantQueryParam(fullUrl, getTenantSubdomain());
  const options = {
    method,
    headers: {},
  };

  // Check if data is FormData (for file uploads)
  const isFormData = data instanceof FormData;

  // Set Content-Type header only if not FormData (browser will set it automatically for FormData)
  if (!isFormData) {
    options.headers['Content-Type'] = 'application/json';
  }

  // Add authentication token if required
  if (requiresAuth) {
    const token = getAccessToken();
    if (token) {
      options.headers['Authorization'] = `Bearer ${token}`;
    } else {
      throw new Error('Authentication required but no token found');
    }
  }

  // Multi-tenant: send tenant subdomain so backend can resolve request.tenant (required for register etc.)
  const tenantSubdomain = getTenantSubdomain();
  if (tenantSubdomain) {
    options.headers['X-Tenant'] = tenantSubdomain;
  }
  // tenant also sent via ensureTenantQueryParam when in dev (proxy does not forward X-Tenant)

  // Add request body for POST/PUT/PATCH, and DELETE when a body is required (e.g. operation delete options)
  if (
    data &&
    (method === 'POST' ||
      method === 'PUT' ||
      method === 'PATCH' ||
      (method === 'DELETE' && data !== null && typeof data === 'object'))
  ) {
    if (isFormData) {
      options.body = data;
    } else {
      options.body = JSON.stringify(data);
    }
  }

  for (let attempt = 0; attempt < RETRY_MAX_ATTEMPTS; attempt++) {
    if (attempt > 0) {
      await new Promise((r) => setTimeout(r, RETRY_DELAY_MS));
    }

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  options.signal = controller.signal;

  let responseReceived = false; // true once fetch() returned (headers received); used to avoid showing "Unable to connect" for body-read failures

  try {
    const response = await fetch(fullUrl, options);
    responseReceived = true;

    // Check if response has content before parsing JSON
    // 204 No Content and 304 Not Modified don't have a body
    let responseData = null;
    
    // Only parse JSON if response has content (not 204 No Content, 304 Not Modified, etc.)
    if (response.status !== 204 && response.status !== 304) {
      // Try to parse JSON, but handle empty responses gracefully
      const text = await response.text();
      if (text.trim()) {
        try {
          responseData = JSON.parse(text);
        } catch (parseError) {
          // If parsing fails, responseData stays null
        }
      }
    }

    if (!response.ok) {
      // Check if token is invalid/expired and we can refresh
      if (response.status === 401 && requiresAuth && responseData && responseData.code === 'token_not_valid') {
        try {
          const newAccessToken = await refreshToken();
          clearTimeout(timeoutId); // original request already completed; stop its timer so only retry timeout is active

          // Retry with a fresh AbortController so the retry has its own full timeout (not the remainder of the original)
          const retryController = new AbortController();
          const retryTimeoutId = setTimeout(() => retryController.abort(), timeoutMs);
          const retryOptions = { ...options, signal: retryController.signal };
          retryOptions.headers = { ...options.headers, Authorization: `Bearer ${newAccessToken}` };

          const retryResponse = await fetch(fullUrl, retryOptions);
          clearTimeout(retryTimeoutId);

          // Parse retry response safely
          let retryResponseData = null;
          if (retryResponse.status !== 204 && retryResponse.status !== 304) {
            const retryText = await retryResponse.text();
            if (retryText.trim()) {
              try {
                retryResponseData = JSON.parse(retryText);
              } catch (e) {
                // Parsing failed, keep as null
              }
            }
          }
          
          if (!retryResponse.ok) {
            clearTimeout(timeoutId);
            throw {
              status: retryResponse.status,
              statusText: retryResponse.statusText,
              data: retryResponseData,
            };
          }
          clearTimeout(timeoutId);
          return retryResponseData;
        } catch (refreshError) {
          // If refresh fails, clear tokens and throw original error
          clearTimeout(timeoutId);
          clearTokens();
          throw {
            status: response.status,
            statusText: response.statusText,
            data: responseData,
          };
        }
      }
      
      // Handle error responses
      clearTimeout(timeoutId);
      const error = {
        status: response.status,
        statusText: response.statusText,
        data: responseData,
      };
      throw error;
    }

    clearTimeout(timeoutId);
    return responseData;
  } catch (error) {
    clearTimeout(timeoutId);
    // Re-throw API error responses (4xx/5xx with status set)
    if (error && error.status) {
      throw error;
    }
    // Timeout: request was aborted after API_REQUEST_TIMEOUT_MS — do not retry
    if (isAbortError(error)) {
      throw {
        status: 0,
        code: 'TIMEOUT',
        statusText: 'Request Timeout',
        data: { error: [TIMEOUT_ERROR_MESSAGE] },
      };
    }
    const isBodyReadError = responseReceived;
    const mappedError = isBodyReadError
      ? { status: 0, code: 'BODY_READ_ERROR', statusText: 'Connection Lost', data: { error: [BODY_READ_ERROR_MESSAGE] } }
      : { status: 0, code: 'NETWORK', statusText: 'Network Error', data: { error: [CONNECTION_ERROR_MESSAGE] } };
    if (attempt < RETRY_MAX_ATTEMPTS - 1) {
      continue;
    }
    throw mappedError;
  }
  }
}

/**
 * Format API errors for UI display
 * @param {object} error - Error object from API call
 * @returns {object} Formatted errors with fieldErrors and general error message
 */
export function formatApiError(error) {
  const formatted = {
    fieldErrors: {},
    generalError: null,
    requiresCaptcha: false,
    captchaSiteKey: null,
  };

  // Brute-force protection: 429 or 401 with requires_captcha (after 5 failed logins)
  const data = error && error.data;
  const isCaptchaRequired = data && (data.requires_captcha === true || data.requires_captcha === 'true');
  if (isCaptchaRequired) {
    formatted.requiresCaptcha = true;
    const key = data.captcha_site_key || data.captchaSiteKey;
    formatted.captchaSiteKey = (typeof key === 'string' && key.trim()) ? key.trim() : null;
  }

  if (error.data) {
    // Django REST Framework error format: {field: [messages]}
    if (typeof error.data === 'object' && !Array.isArray(error.data)) {
      const skipKeys = ['requires_captcha', 'captcha_site_key'];
      Object.keys(error.data).forEach((key) => {
        if (skipKeys.includes(key)) return;
        if (Array.isArray(error.data[key])) {
          formatted.fieldErrors[key] = error.data[key];
        } else if (typeof error.data[key] === 'string') {
          formatted.fieldErrors[key] = [error.data[key]];
        } else if (typeof error.data[key] === 'object' && error.data[key].message) {
          formatted.fieldErrors[key] = [error.data[key].message];
        }
      });
    } else if (typeof error.data === 'string') {
      formatted.generalError = error.data;
    } else if (Array.isArray(error.data)) {
      formatted.generalError = error.data.join(', ');
    } else if (error.data.error) {
      formatted.generalError = Array.isArray(error.data.error) 
        ? error.data.error.join(', ') 
        : error.data.error;
    } else if (error.data.message) {
      formatted.generalError = error.data.message;
    }
    // When captcha required, ensure we have a message
    if (isCaptchaRequired && !formatted.generalError) {
      formatted.generalError = 'Too many failed attempts. Please complete the captcha to continue.';
    }
  }

  // If no specific errors found, use status-appropriate message (never show connection message unless code is NETWORK)
  if (!formatted.generalError && Object.keys(formatted.fieldErrors).length === 0) {
    if (error.status >= 500) {
      formatted.generalError = 'Server error. Please try again later.';
    } else if (error.code === 'TIMEOUT') {
      formatted.generalError = TIMEOUT_ERROR_MESSAGE;
    } else if (error.code === 'BODY_READ_ERROR') {
      formatted.generalError = BODY_READ_ERROR_MESSAGE;
    } else if (error.status === 0 && error.code === 'NETWORK') {
      formatted.generalError = CONNECTION_ERROR_MESSAGE;
    } else {
      formatted.generalError = error.statusText || 'An error occurred. Please try again.';
    }
  }

  // When API returns only field errors (e.g. DRF validation), set generalError so UI does not show generic "Failed to load dashboard data"
  const fieldKeys = Object.keys(formatted.fieldErrors);
  if (!formatted.generalError && fieldKeys.length > 0) {
    const firstMessages = formatted.fieldErrors[fieldKeys[0]];
    formatted.generalError = Array.isArray(firstMessages) ? firstMessages.join(', ') : String(firstMessages || 'Validation error');
  }
  return formatted;
}

/**
 * Login API call. If user has MFA enabled, response will contain requires_mfa: true and temp_token;
 * do not store tokens in that case; show MFA step and call verifyMfa() with temp_token and code.
 * @param {string} email - User email
 * @param {string} password - User password
 * @param {string} [recaptchaToken] - reCAPTCHA token when required (after 5 failed attempts)
 * @returns {Promise} Login response (user, tokens, or requires_mfa + temp_token + user)
 */
export async function login(email, password, recaptchaToken = null) {
  const payload = { email, password };
  if (recaptchaToken) payload.recaptcha_token = recaptchaToken;
  const response = await apiCall('/api/auth/api/v1/login/', 'POST', payload);
  
  // Store tokens only when MFA is not required (or already verified)
  if (response.tokens && !response.requires_mfa) {
    setTokens(response.tokens.access, response.tokens.refresh);
  }
  
  // Store must_change_password flag when we have a full login (tokens)
  if (response.must_change_password !== undefined && response.tokens) {
    setMustChangePassword(response.must_change_password);
  }
  
  return response;
}

/**
 * Verify MFA code and complete login (exchange temp_token + code for real tokens).
 * @param {string} tempToken - Temp token from login response when requires_mfa is true
 * @param {string} code - 6-digit MFA code (TOTP or email)
 * @returns {Promise} Same shape as login: { user, tokens, must_change_password }
 */
export async function verifyMfa(tempToken, code) {
  const response = await apiCall('/api/auth/api/v1/mfa/verify/', 'POST', {
    temp_token: tempToken,
    code: code.trim(),
  });
  if (response.tokens) {
    setTokens(response.tokens.access, response.tokens.refresh);
  }
  if (response.must_change_password !== undefined) {
    setMustChangePassword(response.must_change_password);
  }
  return response;
}

/**
 * Get current user MFA status.
 * @returns {Promise<{ mfa_enabled: boolean, method: string|null }>}
 */
export async function getMfaStatus() {
  return await apiCall('/api/auth/api/v1/mfa/status/', 'GET', null, true);
}

/**
 * Start TOTP setup; returns QR code and secret for authenticator app.
 * @returns {Promise<{ secret: string, provisioning_uri: string, qr_code_base64: string }>}
 */
export async function enableTotpSetup() {
  return await apiCall('/api/auth/api/v1/mfa/enable-totp/', 'POST', null, true);
}

/**
 * Confirm TOTP setup with a code from the authenticator app; enables MFA.
 * @param {string} code - 6-digit code from app
 * @returns {Promise}
 */
export async function confirmTotp(code) {
  return await apiCall('/api/auth/api/v1/mfa/confirm-totp/', 'POST', { code: code.trim() }, true);
}

/**
 * Enable email-based MFA (code sent to email on each login).
 * @returns {Promise}
 */
export async function enableEmailMfa() {
  return await apiCall('/api/auth/api/v1/mfa/enable-email/', 'POST', null, true);
}

/**
 * Disable MFA for current user (requires password).
 * @param {string} password - Current user password
 * @returns {Promise}
 */
export async function disableMfa(password) {
  return await apiCall('/api/auth/api/v1/mfa/disable/', 'POST', { password }, true);
}

/**
 * Admin only: disable MFA for another user (no password required).
 * @param {number} userId - User ID to disable MFA for
 * @returns {Promise}
 */
export async function disableMfaForUser(userId) {
  return await apiCall(`/api/auth/api/v1/mfa/disable-for-user/${userId}/`, 'POST', null, true);
}

/**
 * Register API call
 * @param {object} userData - User registration data
 * @param {string} userData.email - User email
 * @param {string} userData.first_name - User first name
 * @param {string} userData.last_name - User last name
 * @param {string} userData.role - User role (EMPLOYE, MANAGER, ADMIN)
 * @param {number} userData.team_id - Team ID (optional for ADMIN)
 * @returns {Promise} Registration response with user data
 */
export async function register(userData) {
  return await apiCall('/api/auth/api/v1/register/', 'POST', {
    email: userData.email,
    first_name: userData.first_name,
    last_name: userData.last_name,
    role: userData.role,
    team_id: userData.team_id || null,
  });
}

/**
 * Forgot password API call (request reset link)
 * @param {string} email - User email
 * @param {string} [recaptchaToken] - reCAPTCHA token when required (after 5 attempts)
 * @returns {Promise} Response message
 */
export async function forgotPassword(email, recaptchaToken = null) {
  const payload = { email };
  if (recaptchaToken) payload.recaptcha_token = recaptchaToken;
  return await apiCall('/api/auth/api/v1/password/reset/', 'POST', payload);
}

/**
 * Change password API call (authenticated user)
 * @param {string} oldPassword - Current password
 * @param {string} newPassword - New password
 * @param {string} confirmPassword - Password confirmation
 * @returns {Promise} Response message
 */
export async function changePassword(oldPassword, newPassword, confirmPassword) {
  const response = await apiCall(
    '/api/auth/api/v1/password/change/',
    'POST',
    {
      old_password: oldPassword,
      new_password: newPassword,
      confirm_password: confirmPassword,
    },
    true // Requires authentication
  );
  
  // Clear must_change_password flag after successful password change
  clearMustChangePassword();
  
  return response;
}

/**
 * Reset password confirm API call (via email link)
 * @param {string} uid - User ID from email link
 * @param {string} token - Reset token from email link
 * @param {string} newPassword - New password
 * @param {string} confirmPassword - Password confirmation
 * @returns {Promise} Response message
 */
export async function resetPasswordConfirm(uid, token, newPassword, confirmPassword) {
  const response = await apiCall('/api/auth/api/v1/password/reset/confirm/', 'POST', {
    uid,
    token,
    new_password: newPassword,
    confirm_password: confirmPassword,
  });
  
  // Clear must_change_password flag after successful password reset
  // (API also clears it, but we clear it on frontend too for consistency)
  clearMustChangePassword();
  
  return response;
}

/**
 * Refresh access token using refresh token
 * @returns {Promise} New tokens {access, refresh}
 */
async function refreshToken() {
  const refreshTokenValue = getRefreshToken();
  if (!refreshTokenValue) {
    throw new Error('No refresh token available');
  }

  const response = await apiCall('/api/auth/api/v1/token/refresh/', 'POST', {
    refresh: refreshTokenValue,
  }, false); // Don't require auth for refresh endpoint

  if (response.access) {
    // Update access token, keep refresh token if new one provided
    if (response.refresh) {
      setTokens(response.access, response.refresh);
    } else {
      // Only update access token, keep existing refresh token
      localStorage.setItem(getTokenKeys().ACCESS, response.access);
    }
    return response.access;
  }

  throw new Error('Token refresh failed');
}

/**
 * Get stored access token
 * @returns {string|null} Access token or null
 */
export function getAccessToken() {
  return localStorage.getItem(getTokenKeys().ACCESS);
}

/**
 * Get stored refresh token
 * @returns {string|null} Refresh token or null
 */
export function getRefreshToken() {
  return localStorage.getItem(getTokenKeys().REFRESH);
}

/**
 * Store JWT tokens
 * @param {string} accessToken - Access token
 * @param {string} refreshToken - Refresh token
 */
export function setTokens(accessToken, refreshToken) {
  const keys = getTokenKeys();
  localStorage.setItem(keys.ACCESS, accessToken);
  localStorage.setItem(keys.REFRESH, refreshToken);
}

/**
 * Clear stored tokens (logout)
 */
export function clearTokens() {
  const keys = getTokenKeys();
  localStorage.removeItem(keys.ACCESS);
  localStorage.removeItem(keys.REFRESH);
  clearMustChangePassword();
}

/**
 * Check if user is authenticated
 * @returns {boolean} True if access token exists
 */
export function isAuthenticated() {
  return !!getAccessToken();
}

/**
 * Store must_change_password flag
 * @param {boolean} value - Must change password flag
 */
export function setMustChangePassword(value) {
  localStorage.setItem(getTokenKeys().MUST_CHANGE_PASSWORD, JSON.stringify(value));
}

/**
 * Get must_change_password flag
 * @returns {boolean} True if user must change password
 */
export function getMustChangePassword() {
  const value = localStorage.getItem(getTokenKeys().MUST_CHANGE_PASSWORD);
  return value ? JSON.parse(value) : false;
}

/**
 * Clear must_change_password flag
 */
export function clearMustChangePassword() {
  localStorage.removeItem(getTokenKeys().MUST_CHANGE_PASSWORD);
}

/**
 * Get users list with filtering and pagination
 * @param {object} filters - Filter object {role, status, search, centre, team, manager}
 * @param {number} page - Page number (default: 1)
 * @param {number} pageSize - Items per page (default: 20)
 * @returns {Promise} Paginated users response {count, next, previous, results}
 */
export async function getUsers(filters = {}, page = 1, pageSize = 20) {
  const params = new URLSearchParams();
  
  // Add filters
  if (filters.role) params.append('role', filters.role);
  if (filters.status) params.append('status', filters.status);
  if (filters.search) params.append('search', filters.search);
  if (filters.centre) params.append('centre', filters.centre);
  if (filters.team) params.append('team', filters.team);
  if (filters.manager) params.append('manager', filters.manager);
  
  // Add pagination
  params.append('page', page);
  params.append('page_size', pageSize);
  
  const queryString = params.toString();
  const url = `/api/accounts/api/v1/users/${queryString ? `?${queryString}` : ''}`;
  
  return await apiCall(url, 'GET', null, true, { timeoutMs: HEAVY_REQUEST_TIMEOUT_MS });
}

/**
 * Get single user by ID
 * @param {number} userId - User ID
 * @returns {Promise} User object
 */
export async function getUser(userId) {
  return await apiCall(`/api/accounts/api/v1/users/${userId}/`, 'GET', null, true);
}

/**
 * Get current user's team members (for employees)
 * @returns {Promise} Array of team members (excluding current user)
 */
export async function getMyTeamMembers() {
  return await apiCall('/api/accounts/api/v1/my-team-members/', 'GET', null, true);
}

/**
 * Update user
 * @param {number} userId - User ID
 * @param {object} userData - User data to update
 * @returns {Promise} Updated user object
 */
export async function updateUser(userId, userData) {
  return await apiCall(`/api/accounts/api/v1/users/${userId}/`, 'PATCH', userData, true);
}

/**
 * Create user (admin only)
 * Note: The register endpoint generates a random password and sends it via email
 * @param {object} userData - User data to create
 * @param {string} userData.email - User email
 * @param {string} userData.first_name - User first name
 * @param {string} userData.last_name - User last name
 * @param {string} userData.role - User role (EMPLOYE, MANAGER, ADMIN)
 * @param {number} userData.team - Team ID (optional for ADMIN, required for EMPLOYE and MANAGER)
 * @returns {Promise} Created user object
 */
export async function createUser(userData) {
  // Use register endpoint - generates random password and sends via email
  return await apiCall('/api/auth/api/v1/register/', 'POST', {
    email: userData.email,
    first_name: userData.first_name,
    last_name: userData.last_name,
    role: userData.role,
    team_id: userData.team || null,
    centre_id: userData.centre_id || null,
  }, true); // Requires authentication
}

/**
 * Delete user
 * @param {number} userId - User ID
 * @returns {Promise} Response
 */
export async function deleteUser(userId) {
  return await apiCall(`/api/accounts/api/v1/users/${userId}/`, 'DELETE', null, true);
}

/**
 * Assign employee to team (manager only)
 * @param {number} employeeId - Employee ID
 * @param {number} teamId - Team ID
 * @returns {Promise} Response with message and updated employee data
 */
export async function assignEmployeeToTeam(employeeId, teamId) {
  return await apiCall('/api/accounts/api/v1/manager/assign-employee/', 'POST', {
    employee_id: employeeId,
    team_id: teamId
  }, true);
}

/**
 * Remove employee from team (manager only)
 * @param {number} employeeId - Employee ID
 * @param {number} teamId - Team ID
 * @returns {Promise} Response with message and updated employee data
 */
export async function removeEmployeeFromTeam(employeeId, teamId) {
  return await apiCall('/api/accounts/api/v1/manager/remove-employee/', 'POST', {
    employee_id: employeeId,
    team_id: teamId
  }, true);
}

/**
 * Get teams list with optional filters and pagination
 * @param {object} filters - Filter object {centre, manager, name, status}
 * @param {number} page - Page number (default: 1)
 * @param {number} pageSize - Items per page (default: 20)
 * @returns {Promise} Paginated teams response {count, next, previous, results}
 */
export async function getTeams(filters = {}, page = 1, pageSize = 20) {
  const params = new URLSearchParams();
  
  if (filters.centre) params.append('centre', filters.centre);
  if (filters.manager) params.append('manager', filters.manager);
  if (filters.name) params.append('name', filters.name);
  if (filters.status) params.append('status', filters.status);
  
  // Add pagination
  params.append('page', page);
  params.append('page_size', pageSize);
  
  const queryString = params.toString();
  const url = `/api/accounts/api/v1/teams/${queryString ? `?${queryString}` : ''}`;
  
  return await apiCall(url, 'GET', null, true, { timeoutMs: HEAVY_REQUEST_TIMEOUT_MS });
}

/**
 * Get single team by ID
 * @param {number} teamId - Team ID
 * @returns {Promise} Team object
 */
export async function getTeam(teamId) {
  return await apiCall(`/api/accounts/api/v1/teams/${teamId}/`, 'GET', null, true);
}

/**
 * Get teams by centre ID
 * @param {number} centreId - Centre ID
 * @returns {Promise} Array of teams
 */
export async function getTeamsByCentre(centreId) {
  return await apiCall(`/api/accounts/api/v1/teams/by-centre/${centreId}/`, 'GET', null, true);
}

/**
 * Get teams filter list
 * @returns {Promise} Array of teams for filtering
 */
export async function getTeamsFilter() {
  return await apiCall('/api/accounts/api/v1/teams/filter/', 'GET', null, true);
}

/**
 * Update team
 * @param {number} teamId - Team ID
 * @param {object} teamData - Team data to update (name, manager_ids, etc.)
 * @returns {Promise} Updated team object
 */
export async function updateTeam(teamId, teamData) {
  return await apiCall(`/api/accounts/api/v1/teams/${teamId}/`, 'PATCH', teamData, true);
}

/**
 * Create team
 * @param {object} teamData - Team data to create
 * @param {string} teamData.name - Team name (required)
 * @param {number[]} teamData.manager_ids - Manager IDs (optional)
 * @param {string} teamData.description - Team description (optional)
 * @param {boolean} teamData.is_active - Active status (default: true)
 * @returns {Promise} Created team object
 */
export async function createTeam(teamData) {
  return await apiCall('/api/accounts/api/v1/teams/', 'POST', teamData, true);
}

/**
 * Delete team (admin only). Optionally reassign members to another team.
 * Uses query param for target_team_id so it is not lost (DELETE body is often stripped).
 * @param {number} teamId - Team ID to delete
 * @param {object} options - Optional: { target_team_id: number } to reassign all members to that team; omit to leave members without a team
 * @returns {Promise} Resolves on 204
 */
export async function deleteTeam(teamId, options = {}) {
  const hasTarget = options && options.target_team_id != null && options.target_team_id !== '';
  const params = hasTarget ? new URLSearchParams({ target_team_id: String(options.target_team_id) }) : null;
  const url = `/api/accounts/api/v1/teams/${teamId}/${params ? `?${params.toString()}` : ''}`;
  return await apiCall(url, 'DELETE', null, true);
}

/**
 * Get centres list with filters and pagination
 * @param {object} filters - Filter options {name, search, status}
 * @param {number} page - Page number (default: 1)
 * @param {number} pageSize - Items per page (default: 20)
 * @returns {Promise} Paginated centres response {count, next, previous, results}
 */
export async function getCentres(filters = {}, page = 1, pageSize = 20) {
  const params = new URLSearchParams();
  
  if (filters.name) params.append('name', filters.name);
  if (filters.search) params.append('search', filters.search);
  if (filters.status) params.append('status', filters.status);
  
  // Add pagination
  params.append('page', page);
  params.append('page_size', pageSize);
  
  const queryString = params.toString();
  const url = `/api/accounts/api/v1/centres/${queryString ? `?${queryString}` : ''}`;
  
  return await apiCall(url, 'GET', null, true, { timeoutMs: HEAVY_REQUEST_TIMEOUT_MS });
}

/**
 * Get centres filter list
 * @returns {Promise} Array of centres for filtering
 */
export async function getCentresFilter() {
  return await apiCall('/api/accounts/api/v1/centres/filter/', 'GET', null, true);
}

/**
 * Get single centre by ID
 * @param {number} centreId - Centre ID
 * @returns {Promise} Centre object
 */
export async function getCentre(centreId) {
  return await apiCall(`/api/accounts/api/v1/centres/${centreId}/`, 'GET', null, true);
}

/**
 * Create centre
 * @param {object} centreData - Centre data to create
 * @param {string} centreData.name - Centre name (required, must be unique)
 * @param {string} centreData.description - Centre description (optional)
 * @param {boolean} centreData.is_active - Active status (default: true)
 * @returns {Promise} Created centre object
 */
export async function createCentre(centreData) {
  return await apiCall('/api/accounts/api/v1/centres/', 'POST', centreData, true);
}

/**
 * Update centre
 * @param {number} centreId - Centre ID
 * @param {object} centreData - Centre data to update (name, description, is_active)
 * @returns {Promise} Updated centre object
 */
export async function updateCentre(centreId, centreData) {
  return await apiCall(`/api/accounts/api/v1/centres/${centreId}/`, 'PATCH', centreData, true);
}

/**
 * Delete centre (admin only). If centre has employees, pass target_centre_id to reassign them first.
 * @param {number} centreId - Centre ID to delete
 * @param {object} options - Optional: { target_centre_id: number } to reassign all employees to that centre
 * @returns {Promise} Resolves on 204
 */
export async function deleteCentre(centreId, options = {}) {
  const hasTarget = options && options.target_centre_id != null && options.target_centre_id !== '';
  const params = hasTarget ? new URLSearchParams({ target_centre_id: String(options.target_centre_id) }) : null;
  const url = `/api/accounts/api/v1/centres/${centreId}/${params ? `?${params.toString()}` : ''}`;
  return await apiCall(url, 'DELETE', null, true);
}

/**
 * Get operations list with filtering and pagination
 * @param {object} filters - Filter object {code, activity, domain, status, search}
 * @param {number} page - Page number (default: 1)
 * @param {number} pageSize - Items per page (default: 20)
 * @returns {Promise} Paginated operations response {count, next, previous, results}
 */
export async function getOperations(filters = {}, page = 1, pageSize = 20) {
  const params = new URLSearchParams();
  
  // Add filters
  if (filters.production_line_lp) params.append('production_line_lp', filters.production_line_lp);
  if (filters.activity) params.append('activity', filters.activity);
  if (filters.domain) params.append('domain', filters.domain);
  if (filters.production_line_lp) params.append('production_line_lp', filters.production_line_lp);
  if (filters.status) params.append('status', filters.status);
  if (filters.search) params.append('search', filters.search);
  
  // Add pagination
  params.append('page', page);
  params.append('page_size', pageSize);
  
  const queryString = params.toString();
  const url = `/api/operations/api/v1/operations/${queryString ? `?${queryString}` : ''}`;
  
  return await apiCall(url, 'GET', null, true);
}

/**
 * Get active operations as a plain JSON array.
 * @returns {Promise<Array>}
 */
export async function getActiveOperationsJson() {
  return await apiCall('/api/operations/api/v1/operations/active-json/', 'GET', null, false, {
    timeoutMs: HEAVY_REQUEST_TIMEOUT_MS,
  });
}

/**
 * Get single operation by ID
 * @param {number} operationId - Operation ID
 * @returns {Promise} Operation object
 */
export async function getOperation(operationId) {
  return await apiCall(`/api/operations/api/v1/operations/${operationId}/`, 'GET', null, true);
}

/**
 * Create operation
 * @param {object} operationData - Operation data to create {code, activity, domain, description, temps_standard, is_active}
 * @returns {Promise} Created operation object
 */
export async function createOperation(operationData) {
  return await apiCall('/api/operations/api/v1/operations/', 'POST', operationData, true);
}

/**
 * Update operation
 * @param {number} operationId - Operation ID
 * @param {object} operationData - Operation data to update {code, activity, domain, description, temps_standard}
 * @returns {Promise} Updated operation object
 */
export async function updateOperation(operationId, operationData) {
  return await apiCall(`/api/operations/api/v1/operations/${operationId}/`, 'PATCH', operationData, true);
}

/**
 * Toggle operation active status
 * @param {number} operationId - Operation ID
 * @param {boolean} isActive - New active status
 * @returns {Promise} Updated operation object
 */
export async function toggleOperationStatus(operationId, isActive) {
  return await apiCall(`/api/operations/api/v1/operations/${operationId}/`, 'PATCH', { is_active: isActive }, true);
}

/**
 * RS tenant admin: imputation count linked to an operation before delete (for confirmation UI).
 * @param {number} operationId
 * @returns {Promise<{ imputation_count: number }>}
 */
export async function getOperationDeleteInfo(operationId) {
  return await apiCall(`/api/operations/api/v1/operations/${operationId}/delete-info/`, 'GET', null, true);
}

/**
 * RS tenant admin: delete an operation. When imputations exist, pass related_entries and optional target_operation_id.
 * @param {number} operationId
 * @param {object|null} [payload] - null when no related imputations; otherwise related_entries (+ target_operation_id for reassign)
 */
export async function deleteOperation(operationId, payload = null) {
  return await apiCall(`/api/operations/api/v1/operations/${operationId}/`, 'DELETE', payload, true);
}

/**
 * Get workflows list
 * @returns {Promise} Array of workflow objects
 */
export async function getWorkflows() {
  return await apiCall('/api/operations/api/v1/workflows/', 'GET', null, true);
}

/**
 * Create a new workflow
 * @param {object} data - { code, label, project_code, digits_in_project_code }
 * @returns {Promise} Created workflow object
 */
export async function createWorkflow(data) {
  return await apiCall('/api/operations/api/v1/workflows/', 'POST', data, true);
}

/**
 * Update a workflow
 * @param {number} id - Workflow ID
 * @param {object} data - { code, label, project_code, digits_in_project_code }
 * @returns {Promise} Updated workflow object
 */
export async function updateWorkflow(id, data) {
  return await apiCall(`/api/operations/api/v1/workflows/${id}/`, 'PATCH', data, true);
}

/**
 * Delete a workflow
 * @param {number} id - Workflow ID
 * @returns {Promise}
 */
export async function deleteWorkflow(id) {
  return await apiCall(`/api/operations/api/v1/workflows/${id}/`, 'DELETE', null, true);
}

/**
 * Get operation history list with filters and pagination
 * @param {object} filters - Filter options {action_type, date_from, date_to, user, operation}
 * @param {number} page - Page number (default: 1)
 * @param {number} pageSize - Items per page (default: 50)
 * @returns {Promise} Paginated operation history response {count, next, previous, results}
 */
export async function getOperationHistory(filters = {}, page = 1, pageSize = 50) {
  const params = new URLSearchParams();
  
  if (filters.action_type) params.append('action_type', filters.action_type);
  if (filters.date_from) params.append('date_from', filters.date_from);
  if (filters.date_to) params.append('date_to', filters.date_to);
  if (filters.user) params.append('user', filters.user);
  if (filters.operation) params.append('operation', filters.operation);
  if (filters.production_line_lp) params.append('production_line_lp', filters.production_line_lp);
  if (filters.program) params.append('program', filters.program);
  if (filters.project_type) params.append('project_type', filters.project_type);
  
  // Add pagination
  params.append('page', page);
  params.append('page_size', pageSize);
  
  const queryString = params.toString();
  const url = `/api/operations/api/v1/operations/history/${queryString ? `?${queryString}` : ''}`;
  
  return await apiCall(url, 'GET', null, true, { timeoutMs: HEAVY_REQUEST_TIMEOUT_MS });
}

/**
 * Get Jira tickets table (admin only) with filtering and pagination
 * @param {object} filters - Filter options {ticket_id, projet, utilisateur, email, search, missing_imputation}
 * @param {number} page - Page number (default: 1)
 * @param {number} pageSize - Items per page (default: 20)
 * @returns {Promise<{ count, results, current_page, total_pages }>}
 */
export async function getJiraTickets(filters = {}, page = 1, pageSize = 20) {
  const params = new URLSearchParams();

  if (filters.search) params.append('search', filters.search);
  if (filters.employee) params.append('employee', filters.employee);
  if (filters.date_from) params.append('date_from', filters.date_from);
  if (filters.date_to) params.append('date_to', filters.date_to);

  params.append('page', String(page));
  params.append('page_size', String(pageSize));

  const queryString = params.toString();
  const url = `/api/operations/api/v1/jira-tickets/${queryString ? `?${queryString}` : ''}`;
  return await apiCall(url, 'GET', null, true, { timeoutMs: HEAVY_REQUEST_TIMEOUT_MS });
}

/**
 * Aggregate stats for Jira tickets (same filter params as getJiraTickets).
 * @param {object} filters - { search, employee, date_from, date_to }
 */
export async function getJiraTicketsStats(filters = {}) {
  const params = new URLSearchParams();
  if (filters.search) params.append('search', filters.search);
  if (filters.employee) params.append('employee', filters.employee);
  if (filters.date_from) params.append('date_from', filters.date_from);
  if (filters.date_to) params.append('date_to', filters.date_to);
  const queryString = params.toString();
  const url = `/api/operations/api/v1/jira-tickets/stats/${queryString ? `?${queryString}` : ''}`;
  return await apiCall(url, 'GET', null, true, { timeoutMs: HEAVY_REQUEST_TIMEOUT_MS });
}

/**
 * Partially update a Jira ticket (RS admin; editable fields only).
 * @param {number} id - JiraTicket primary key
 * @param {object} data - { title, assigned_user_email, previous_status, new_status, ticket_description }
 */
export async function patchJiraTicket(id, data) {
  return await apiCall(
    `/api/operations/api/v1/jira-tickets/${id}/`,
    'PATCH',
    data,
    true,
    { timeoutMs: HEAVY_REQUEST_TIMEOUT_MS }
  );
}

/**
 * Create an Imputation linked to a Jira ticket (RS admin). Validates operation title, assignee, statuses server-side.
 */
export async function insertJiraImputation(ticketId, data) {
  return await apiCall(
    `/api/operations/api/v1/jira-tickets/${ticketId}/insert-imputation/`,
    'POST',
    data,
    true,
    { timeoutMs: HEAVY_REQUEST_TIMEOUT_MS }
  );
}

/**
 * Get Jira comments list (RS admin) with filtering and pagination.
 * @param {object} filters - { search, employee, date_from, date_to }
 * @param {number} page - Page number (default: 1)
 * @param {number} pageSize - Items per page (default: 20)
 * @returns {Promise<{ count, results, current_page, total_pages }>}
 */
export async function getJiraComments(filters = {}, page = 1, pageSize = 20) {
  const params = new URLSearchParams();

  if (filters.search) params.append('search', filters.search);
  if (filters.employee) params.append('employee', filters.employee);
  if (filters.date_from) params.append('date_from', filters.date_from);
  if (filters.date_to) params.append('date_to', filters.date_to);

  params.append('page', String(page));
  params.append('page_size', String(pageSize));

  const queryString = params.toString();
  const url = `/api/jira-comments${queryString ? `?${queryString}` : ''}`;
  return await apiCall(url, 'GET', null, true, { timeoutMs: HEAVY_REQUEST_TIMEOUT_MS });
}

/**
 * Get aggregate statistics for Jira comments (RS admin), respecting list filters.
 * @param {object} filters - Same shape as getJiraComments filters
 * @returns {Promise<{ total, imputations_created, missing_imputation, with_imputation_error, unique_authors, imputation_rate }>}
 */
export async function getJiraCommentsStats(filters = {}) {
  const params = new URLSearchParams();

  if (filters.search) params.append('search', filters.search);
  if (filters.employee) params.append('employee', filters.employee);
  if (filters.date_from) params.append('date_from', filters.date_from);
  if (filters.date_to) params.append('date_to', filters.date_to);

  const queryString = params.toString();
  const url = `/api/jira-comments/stats${queryString ? `?${queryString}` : ''}`;
  return await apiCall(url, 'GET', null, true, { timeoutMs: HEAVY_REQUEST_TIMEOUT_MS });
}

/**
 * Employees with imputations in the given date_operation range (RS Jira comments page).
 * @param {{ date_from?: string, date_to?: string }} params
 * @returns {Promise<{ results: Array<{ id, first_name, last_name, email, role }> }>}
 */
export async function getJiraCommentEmployees({ date_from, date_to } = {}) {
  const params = new URLSearchParams();
  if (date_from) params.append('date_from', date_from);
  if (date_to) params.append('date_to', date_to);
  const queryString = params.toString();
  const url = `/api/jira-comments/employees${queryString ? `?${queryString}` : ''}`;
  return await apiCall(url, 'GET', null, true, { timeoutMs: HEAVY_REQUEST_TIMEOUT_MS });
}

/**
 * Create Jira comment record with Jira payload shape.
 * Does not require auth (matches backend AllowAny on POST).
 * @param {object} payload
 */
export async function createJiraComment(payload) {
  return await apiCall('/api/jira-comments', 'POST', payload, false, { timeoutMs: HEAVY_REQUEST_TIMEOUT_MS });
}

/**
 * Detect related operation for a Jira comment using backend main.py matcher.
 * @param {string} comment
 * @returns {Promise<{activity: string|null, operation: object|null}>}
 */
export async function detectJiraCommentOperation(comment) {
  return await apiCall(
    '/api/jira-comments/detect-operation',
    'POST',
    { comment },
    true,
    { timeoutMs: HEAVY_REQUEST_TIMEOUT_MS }
  );
}

/**
 * Insert imputation from Jira comment detected fields.
 * @param {{comment_id: number, assigned_user_email: string, operation_id: number, comment?: string}} payload
 */
export async function insertJiraCommentImputation(payload) {
  return await apiCall(
    '/api/jira-comments/insert-imputation',
    'POST',
    payload,
    true,
    { timeoutMs: HEAVY_REQUEST_TIMEOUT_MS }
  );
}

/**
 * Get current user profile
 * @returns {Promise} Current user profile object
 */
export async function getCurrentUserProfile() {
  return await apiCall('/api/auth/api/v1/profile/', 'GET', null, true);
}

/**
 * Update current user profile
 * @param {object} userData - User data to update (first_name, last_name, profile_picture)
 * @param {File} userData.profile_picture - Optional profile picture file
 * @returns {Promise} Updated user profile object
 */
export async function updateCurrentUserProfile(userData) {
  // Check if profile_picture is a File object (file upload)
  if (userData.profile_picture instanceof File) {
    const formData = new FormData();
    
    // Add text fields
    if (userData.first_name !== undefined) {
      formData.append('first_name', userData.first_name);
    }
    if (userData.last_name !== undefined) {
      formData.append('last_name', userData.last_name);
    }
    
    // Add profile picture file
    formData.append('profile_picture', userData.profile_picture);
    
    return await apiCall('/api/auth/api/v1/profile/', 'PATCH', formData, true);
  }
  
  // Regular JSON update (no file)
  return await apiCall('/api/auth/api/v1/profile/', 'PATCH', userData, true);
}

/**
 * Update profile picture only
 * @param {File} file - Profile picture file
 * @returns {Promise} Updated user profile object
 */
export async function updateProfilePicture(file) {
  if (!(file instanceof File)) {
    throw new Error('Invalid file provided');
  }
  
  const formData = new FormData();
  formData.append('profile_picture', file);
  
    return await apiCall('/api/auth/api/v1/profile/', 'PATCH', formData, true);
}

/**
 * Get badge awards for current user
 * @returns {Promise} Badge awards list
 */
export async function getBadgeAwards() {
  return await apiCall('/api/accounts/api/v1/badges/', 'GET', null, true);
}

/**
 * Get badge configurations (managers and admins only)
 * @param {object} filters - Optional filters (is_global, badge_type, is_active)
 * @returns {Promise} Badge configurations list
 */
export async function getBadgeConfigurations(filters = {}) {
  const queryParams = new URLSearchParams();
  if (filters.is_global !== undefined) {
    queryParams.append('is_global', filters.is_global.toString());
  }
  if (filters.badge_type) {
    queryParams.append('badge_type', filters.badge_type);
  }
  if (filters.is_active !== undefined) {
    queryParams.append('is_active', filters.is_active.toString());
  }
  const queryString = queryParams.toString();
  const url = `/api/accounts/api/v1/badge-configurations/${queryString ? '?' + queryString : ''}`;
  return await apiCall(url, 'GET', null, true);
}

/**
 * Get single badge configuration
 * @param {number} badgeId - Badge ID
 * @returns {Promise} Badge configuration
 */
export async function getBadgeConfiguration(badgeId) {
  return await apiCall(`/api/accounts/api/v1/badge-configurations/${badgeId}/`, 'GET', null, true);
}

/**
 * Update badge configuration
 * @param {number} badgeId - Badge ID
 * @param {object} configData - Configuration data to update
 * @param {File} configData.icon - Optional badge icon file
 * @returns {Promise} Updated badge configuration
 */
export async function updateBadgeConfiguration(badgeId, configData) {
  // Check if icon is a File object (file upload) or explicitly null (to remove)
  if (configData.icon instanceof File) {
    const formData = new FormData();
    
    // Add all text fields
    Object.keys(configData).forEach(key => {
      if (key !== 'icon' && configData[key] !== undefined) {
        if (Array.isArray(configData[key])) {
          // Handle arrays (like applicable_team_ids)
          configData[key].forEach(item => {
            formData.append(key, item);
          });
        } else if (configData[key] !== null) {
          formData.append(key, configData[key]);
        }
      }
    });
    
    // Add icon file
    formData.append('icon', configData.icon);
    
    return await apiCall(`/api/accounts/api/v1/badge-configurations/${badgeId}/`, 'PATCH', formData, true);
  }
  
  // Regular JSON update (no file, but may include icon: null to remove)
  return await apiCall(`/api/accounts/api/v1/badge-configurations/${badgeId}/`, 'PATCH', configData, true);
}

/**
 * Create new badge configuration
 * @param {object} configData - Configuration data
 * @param {File} configData.icon - Optional badge icon file
 * @returns {Promise} Created badge configuration
 */
export async function createBadgeConfiguration(configData) {
  // Check if icon is a File object (file upload)
  if (configData.icon instanceof File) {
    const formData = new FormData();
    
    // Add all text fields
    Object.keys(configData).forEach(key => {
      if (key !== 'icon' && configData[key] !== undefined) {
        if (Array.isArray(configData[key])) {
          // Handle arrays (like applicable_team_ids)
          configData[key].forEach(item => {
            formData.append(key, item);
          });
        } else if (configData[key] !== null) {
          formData.append(key, configData[key]);
        }
      }
    });
    
    // Add icon file
    formData.append('icon', configData.icon);
    
    return await apiCall('/api/accounts/api/v1/badge-configurations/', 'POST', formData, true);
  }
  
  // Regular JSON create (no file)
  return await apiCall('/api/accounts/api/v1/badge-configurations/', 'POST', configData, true);
}

/**
 * Get badge configuration history
 * @param {number} badgeId - Badge ID
 * @param {object} filters - Optional filters (change_type)
 * @returns {Promise} Change history list
 */
export async function getBadgeConfigurationHistory(badgeId, filters = {}) {
  const queryParams = new URLSearchParams();
  queryParams.append('badge_id', badgeId.toString());
  if (filters.change_type) {
    queryParams.append('change_type', filters.change_type);
  }
  return await apiCall(`/api/accounts/api/v1/badge-configurations/history/?${queryParams.toString()}`, 'GET', null, true);
}

/**
 * Test trigger badge award for a specific badge configuration
 * @param {number} badgeId - Badge ID
 * @param {object} options - Optional parameters
 * @param {string} options.target_date - Date string (YYYY-MM-DD) for DAILY badges
 * @param {string} options.target_week_start - Date string (YYYY-MM-DD) for WEEKLY badges
 * @param {number} options.target_month - Integer (1-12) for MONTHLY badges
 * @param {number} options.target_year - Integer for MONTHLY badges
 * @returns {Promise} Result with success status and awarded employees
 */
export async function testTriggerBadgeAward(badgeId, options = {}) {
  // Default to evaluation only (apply: false) unless explicitly set
  const payload = {
    apply: false,
    ...options
  };
  
  return await apiCall(`/api/accounts/api/v1/badge-configurations/${badgeId}/test-trigger/`, 'POST', payload, true);
}

/**
 * Get badge rules for employees (read-only)
 * @returns {Promise} Badge rules with version and effective date
 */
export async function getBadgeRules() {
  return await apiCall('/api/accounts/api/v1/badge-rules/', 'GET', null, true);
}

/**
 * Get a single badge rule detail by ID
 * @param {number} badgeId - Badge ID
 * @returns {Promise} Badge rule details with version and history
 */
export async function getBadgeRule(badgeId) {
  return await apiCall(`/api/accounts/api/v1/badge-rules/${badgeId}/`, 'GET', null, true);
}

/**
 * Create a badge award dispute
 * @param {number} badgeAwardId - Badge award ID
 * @param {string} reason - Mandatory reason for dispute
 * @returns {Promise} Created dispute
 */
export async function createBadgeAwardDispute(badgeAwardId, reason) {
  return await apiCall(`/api/accounts/api/v1/badge-awards/${badgeAwardId}/dispute/`, 'POST', {
    badge_award_id: badgeAwardId,
    reason: reason
  }, true);
}

/**
 * Get badge award disputes
 * @param {object} filters - Optional filters (status)
 * @returns {Promise} Disputes list
 */
export async function getBadgeAwardDisputes(filters = {}) {
  const queryParams = new URLSearchParams();
  if (filters.status) {
    queryParams.append('status', filters.status);
  }
  const queryString = queryParams.toString();
  const url = `/api/accounts/api/v1/badge-award-disputes/${queryString ? '?' + queryString : ''}`;
  return await apiCall(url, 'GET', null, true);
}

/**
 * Get a specific badge award dispute
 * @param {number} disputeId - Dispute ID
 * @returns {Promise} Dispute details
 */
export async function getBadgeAwardDispute(disputeId) {
  return await apiCall(`/api/accounts/api/v1/badge-award-disputes/${disputeId}/`, 'GET', null, true);
}

/**
 * Update badge award dispute (managers/admins only)
 * @param {number} disputeId - Dispute ID
 * @param {object} data - Update data (status, manager_notes, resolution_notes)
 * @returns {Promise} Updated dispute
 */
export async function updateBadgeAwardDispute(disputeId, data) {
  return await apiCall(`/api/accounts/api/v1/badge-award-disputes/${disputeId}/`, 'PATCH', data, true);
}

/**
 * Get notifications for current user
 * @param {object} filters - Optional filters { is_read, search, page, page_size, date_from, date_to }
 * @returns {Promise} Paginated notifications { count, next, previous, results, current_page, total_pages, page_size }
 */
export async function getNotifications(filters = {}) {
  const queryParams = new URLSearchParams();
  if (filters.is_read !== undefined && filters.is_read !== null && filters.is_read !== '') {
    queryParams.append('is_read', filters.is_read.toString());
  }
  if (filters.search && String(filters.search).trim()) {
    queryParams.append('search', String(filters.search).trim());
  }
  if (filters.date_from && String(filters.date_from).trim()) {
    queryParams.append('date_from', String(filters.date_from).trim());
  }
  if (filters.date_to && String(filters.date_to).trim()) {
    queryParams.append('date_to', String(filters.date_to).trim());
  }
  if (filters.page != null) {
    queryParams.append('page', String(filters.page));
  }
  if (filters.page_size != null) {
    queryParams.append('page_size', String(filters.page_size));
  }
  const queryString = queryParams.toString();
  const url = `/api/accounts/api/v1/notifications/${queryString ? '?' + queryString : ''}`;
  return await apiCall(url, 'GET', null, true);
}

/**
 * Mark a notification as read
 * @param {number} notificationId - Notification ID
 * @returns {Promise} Updated notification
 */
export async function markNotificationRead(notificationId) {
  return await apiCall(`/api/accounts/api/v1/notifications/${notificationId}/read/`, 'PATCH', null, true);
}

/**
 * Mark all notifications as read for current user
 * @returns {Promise} Response with updated count
 */
export async function markAllNotificationsRead() {
  return await apiCall('/api/accounts/api/v1/notifications/mark-all-read/', 'POST', null, true);
}

/**
 * Remove profile picture
 * @returns {Promise} Updated user profile object
 */
export async function removeProfilePicture() {
  // Send null as a string in JSON since the backend expects it
  return await apiCall('/api/auth/api/v1/profile/', 'PATCH', { profile_picture: null }, true);
}

/**
 * Get imputations list with filtering and pagination
 * @param {object} filters - Filter object {date_from, date_to, gcr, operation, etat_final}
 * @param {number} page - Page number (default: 1)
 * @param {number} pageSize - Items per page (default: 20)
 * @returns {Promise} Paginated imputations response {count, next, previous, results}
 */
export async function getImputations(filters = {}, page = 1, pageSize = 20) {
  const params = new URLSearchParams();
  
  // Add filters
  if (filters.date_from) params.append('date_from', filters.date_from);
  if (filters.date_to) params.append('date_to', filters.date_to);
  if (filters.gcr) params.append('gcr', filters.gcr);
  if (filters.operation) params.append('operation', filters.operation);
  if (filters.production_line_lp) params.append('production_line_lp', filters.production_line_lp);
  if (filters.project_type) params.append('project_type', filters.project_type);
  if (filters.program) params.append('program', filters.program);
  if (filters.etat_final) params.append('etat_final', filters.etat_final);
  
  // Add pagination
  params.append('page', page);
  params.append('page_size', pageSize);
  
  const queryString = params.toString();
  const url = `/api/imputations/api/v1/imputations/${queryString ? `?${queryString}` : ''}`;
  
  return await apiCall(url, 'GET', null, true, { timeoutMs: HEAVY_REQUEST_TIMEOUT_MS });
}

/**
 * Get single imputation by ID
 * @param {number} imputationId - Imputation ID
 * @returns {Promise} Imputation object
 */
export async function getImputation(imputationId) {
  return await apiCall(`/api/imputations/api/v1/imputations/${imputationId}/`, 'GET', null, true);
}

/**
 * Check if an imputation would be a duplicate (same employee, project code, date)
 * @param {string} date_operation - Operation date (YYYY-MM-DD)
 * @param {string} gcr - Full project code (e.g. "GCR-INT-123456ABC")
 * @returns {Promise<{ duplicate: boolean }>}
 */
/**
 * Check if an imputation would be a duplicate (same employee, project code, date).
 * If operationId is provided, also returns max_reached, current_count, max_allowed for
 * (employee, operation, project code, date) so the UI can block when max duplicates reached.
 * @param {string} date_operation - YYYY-MM-DD
 * @param {string} gcr - Project code
 * @param {number} [operationId] - Operation ID (optional, enables max_reached / current_count / max_allowed)
 * @returns {Promise<{ duplicate: boolean, max_reached?: boolean, current_count?: number, max_allowed?: number }>}
 */
export async function checkImputationDuplicate(date_operation, gcr, operationId) {
  const params = new URLSearchParams({ date_operation, gcr });
  if (operationId != null) params.append('operation', String(operationId));
  return await apiCall(`/api/imputations/api/v1/imputations/check-duplicate/?${params}`, 'GET', null, true);
}

/**
 * Create imputation
 * @param {object} imputationData - Imputation data to create
 * @param {string} imputationData.date_operation - Operation date (YYYY-MM-DD)
 * @param {string} imputationData.gcr - GCR/GIR code (e.g., "GCR-12345")
 * @param {number} imputationData.operation_id - Operation ID (required)
 * @param {string} imputationData.etat_final - Final state (TERMINE, ECHEC; legacy ANNULE, EN_ATTENTE)
 * @param {string} imputationData.commentaire - Optional comment
 * @returns {Promise} Created imputation object
 */
export async function createImputation(imputationData) {
  return await apiCall('/api/imputations/api/v1/imputations/', 'POST', imputationData, true);
}

/**
 * Update imputation
 * @param {number} imputationId - Imputation ID
 * @param {object} imputationData - Imputation data to update
 * @param {string} imputationData.date_operation - Operation date (YYYY-MM-DD)
 * @param {string} imputationData.gcr - GCR/GIR code
 * @param {number} imputationData.operation_id - Operation ID
 * @param {string} imputationData.etat_final - Final state
 * @param {string} imputationData.commentaire - Optional comment
 * @param {string} imputationData.modification_reason - Optional modification reason
 * @returns {Promise} Updated imputation object
 */
export async function updateImputation(imputationId, imputationData) {
  return await apiCall(`/api/imputations/api/v1/imputations/${imputationId}/`, 'PATCH', imputationData, true);
}

/**
 * Delete imputation
 * @param {number} imputationId - Imputation ID
 * @returns {Promise} Empty response on success
 */
export async function deleteImputation(imputationId) {
  return await apiCall(`/api/imputations/api/v1/imputations/${imputationId}/`, 'DELETE', null, true);
}

/**
 * List planning events for the current user in a date range.
 * @param {object} filters - { date_from, date_to } as YYYY-MM-DD
 * @returns {Promise<object[]>}
 */
export async function getEvents(filters = {}) {
  const params = new URLSearchParams();
  if (filters.date_from) params.append('date_from', filters.date_from);
  if (filters.date_to) params.append('date_to', filters.date_to);
  if (filters.employe != null && filters.employe !== '') {
    params.append('employe', filters.employe);
  }
  const queryString = params.toString();
  const url = `/api/planning/api/v1/events/${queryString ? `?${queryString}` : ''}`;
  return await apiCall(url, 'GET', null, true);
}

/**
 * Create a planning event for the current user.
 * @param {object} eventData - { day, title, start, end, category }
 * @returns {Promise<object>}
 */
export async function createEvent(eventData) {
  return await apiCall('/api/planning/api/v1/events/', 'POST', eventData, true);
}

/**
 * Update a planning event.
 * @param {number|string} eventId
 * @param {object} eventData - partial { day, title, start, end, category }
 * @returns {Promise<object>}
 */
export async function updateEvent(eventId, eventData) {
  return await apiCall(`/api/planning/api/v1/events/${eventId}/`, 'PATCH', eventData, true);
}

/**
 * Delete a planning event.
 * @param {number|string} eventId
 * @returns {Promise}
 */
export async function deleteEvent(eventId) {
  return await apiCall(`/api/planning/api/v1/events/${eventId}/`, 'DELETE', null, true);
}

/**
 * Outlook connection status for the current user.
 * When not connected, may include authorize_url to complete Microsoft OAuth.
 * @param {string} [returnTo] - relative frontend path after OAuth callback
 * @returns {Promise<{configured: boolean, connected: boolean, reason?: string, authorize_url?: string, can_connect?: boolean}>}
 */
export async function getOutlookStatus(returnTo) {
  const params = new URLSearchParams();
  if (returnTo) params.append('return_to', returnTo);
  const qs = params.toString();
  return await apiCall(
    `/api/planning/api/v1/outlook/status/${qs ? `?${qs}` : ''}`,
    'GET',
    null,
    true
  );
}

/**
 * Get Microsoft OAuth authorize URL to connect Outlook.
 * @param {string} [returnTo] - relative frontend path after callback
 * @returns {Promise<{authorize_url: string}>}
 */
export async function getOutlookConnectUrl(returnTo) {
  const params = new URLSearchParams();
  if (returnTo) params.append('return_to', returnTo);
  const qs = params.toString();
  return await apiCall(
    `/api/planning/api/v1/outlook/connect/${qs ? `?${qs}` : ''}`,
    'GET',
    null,
    true
  );
}

/**
 * Disconnect Outlook for the current user.
 * @returns {Promise<{disconnected: boolean}>}
 */
export async function disconnectOutlook() {
  return await apiCall('/api/planning/api/v1/outlook/disconnect/', 'POST', null, true);
}

/**
 * Preview Outlook events for a week (Monday week_start).
 * @param {string} weekStart - YYYY-MM-DD
 * @returns {Promise<{week_start: string, week_end: string, events: object[]}>}
 */
export async function previewOutlookWeek(weekStart) {
  const params = new URLSearchParams({ week_start: weekStart });
  return await apiCall(
    `/api/planning/api/v1/outlook/preview/?${params.toString()}`,
    'GET',
    null,
    true
  );
}

/**
 * Import selected Outlook events into planning.
 * @param {object} payload - { week_start, outlook_event_ids: string[], category?: string }
 * @returns {Promise<object>}
 */
export async function importOutlookEvents(payload) {
  return await apiCall('/api/planning/api/v1/outlook/import/', 'POST', payload, true);
}

/**
 * List planning event categories.
 * @param {object} filters - optional { is_active }
 * @returns {Promise<object[]>}
 */
export async function getCategories(filters = {}) {
  const params = new URLSearchParams();
  if (filters.is_active != null && filters.is_active !== '') {
    params.append('is_active', filters.is_active);
  }
  const queryString = params.toString();
  const url = `/api/planning/api/v1/categories/${queryString ? `?${queryString}` : ''}`;
  return await apiCall(url, 'GET', null, true);
}

/**
 * Create a planning event category (admin only).
 * @param {object} categoryData
 * @returns {Promise<object>}
 */
export async function createCategory(categoryData) {
  return await apiCall('/api/planning/api/v1/categories/', 'POST', categoryData, true);
}

/**
 * Update a planning event category (admin only).
 * @param {number|string} categoryId
 * @param {object} categoryData
 * @returns {Promise<object>}
 */
export async function updateCategory(categoryId, categoryData) {
  return await apiCall(`/api/planning/api/v1/categories/${categoryId}/`, 'PATCH', categoryData, true);
}

/**
 * Delete a planning event category (admin only).
 * If the category has events, pass { target_category_id } to reassign them.
 * @param {number|string} categoryId
 * @param {object} options - { target_category_id }
 * @returns {Promise}
 */
export async function deleteCategory(categoryId, options = {}) {
  const hasTarget = options && options.target_category_id != null && options.target_category_id !== '';
  const params = hasTarget
    ? new URLSearchParams({ target_category_id: String(options.target_category_id) })
    : null;
  const url = `/api/planning/api/v1/categories/${categoryId}/${params ? `?${params.toString()}` : ''}`;
  return await apiCall(url, 'DELETE', null, true);
}

/**
 * Get weekly planning reminder schedule for the current tenant (admin only).
 * @returns {Promise<{day_of_week:number,hour:number,minute:number,enabled:boolean,timezone:string}>}
 */
export async function getReminderSchedule() {
  return await apiCall('/api/planning/api/v1/teams-planning/reminder-schedule/', 'GET', null, true);
}

/**
 * Update weekly planning reminder schedule for the current tenant (admin only).
 * @param {object} scheduleData - { day_of_week, hour, minute, enabled }
 * @returns {Promise<object>}
 */
export async function updateReminderSchedule(scheduleData) {
  return await apiCall(
    '/api/planning/api/v1/teams-planning/reminder-schedule/',
    'PATCH',
    scheduleData,
    true
  );
}

/**
 * Teams planning dashboard stats (managers/admins).
 * @param {object} filters - { date_from, date_to, team, centre, employee, hours_per_day }
 */
export async function getTeamsPlanningStats(filters = {}) {
  const params = new URLSearchParams();
  if (filters.date_from) params.append('date_from', filters.date_from);
  if (filters.date_to) params.append('date_to', filters.date_to);
  if (filters.team && filters.team !== 'All') params.append('team', filters.team);
  if (filters.centre && filters.centre !== 'All') params.append('centre', filters.centre);
  if (filters.employee && filters.employee !== 'All') params.append('employee', filters.employee);
  if (filters.member && filters.member !== 'All') params.append('employee', filters.member);
  if (filters.hours_per_day != null && filters.hours_per_day !== '') {
    params.append('hours_per_day', filters.hours_per_day);
  }
  if (filters.working_days && filters.working_days !== '0,1,2,3,4') {
    params.append('working_days', filters.working_days);
  }
  const queryString = params.toString();
  const url = `/api/planning/api/v1/teams-planning/stats/${queryString ? `?${queryString}` : ''}`;
  return await apiCall(url, 'GET', null, true, { timeoutMs: HEAVY_REQUEST_TIMEOUT_MS });
}

/**
 * Pending declaration users for Teams planning.
 * @param {object} filters - { date_from, date_to, team, centre, employee }
 */
export async function getTeamsPlanningPending(filters = {}) {
  const params = new URLSearchParams();
  if (filters.date_from) params.append('date_from', filters.date_from);
  if (filters.date_to) params.append('date_to', filters.date_to);
  if (filters.team && filters.team !== 'All') params.append('team', filters.team);
  if (filters.centre && filters.centre !== 'All') params.append('centre', filters.centre);
  if (filters.employee && filters.employee !== 'All') params.append('employee', filters.employee);
  if (filters.member && filters.member !== 'All') params.append('employee', filters.member);
  if (filters.working_days && filters.working_days !== '0,1,2,3,4') {
    params.append('working_days', filters.working_days);
  }
  const queryString = params.toString();
  const url = `/api/planning/api/v1/teams-planning/pending-declarations/${queryString ? `?${queryString}` : ''}`;
  return await apiCall(url, 'GET', null, true, { timeoutMs: HEAVY_REQUEST_TIMEOUT_MS });
}

/**
 * Send declaration reminder emails.
 * @param {number[]} userIds
 * @param {object} filters - { date_from, date_to, team, centre, employee }
 */
export async function sendTeamsPlanningReminders(userIds, filters = {}) {
  return await apiCall('/api/planning/api/v1/teams-planning/remind/', 'POST', {
    user_ids: userIds,
    date_from: filters.date_from || undefined,
    date_to: filters.date_to || undefined,
    working_days: filters.working_days || undefined,
    team: filters.team && filters.team !== 'All' ? filters.team : undefined,
    centre: filters.centre && filters.centre !== 'All' ? filters.centre : undefined,
    employee:
      (filters.employee && filters.employee !== 'All' && filters.employee) ||
      (filters.member && filters.member !== 'All' && filters.member) ||
      undefined,
  }, true);
}

/**
 * Export Teams Planning events for filtered users/date range.
 * @param {object} filters - { date_from, date_to, team, centre, member, working_days }
 * @param {string} format - 'csv' | 'xlsx'
 * @returns {Promise<{ blob: Blob, filename: string }>}
 */
export async function exportTeamsPlanningEvents(filters = {}, format = 'csv') {
  const normalizedFormat = format === 'excel' ? 'xlsx' : format;
  const params = new URLSearchParams();
  params.append('export_format', normalizedFormat || 'csv');
  if (filters.date_from) params.append('date_from', filters.date_from);
  if (filters.date_to) params.append('date_to', filters.date_to);
  if (filters.team && filters.team !== 'All') params.append('team', filters.team);
  if (filters.centre && filters.centre !== 'All') params.append('centre', filters.centre);
  if (filters.employee && filters.employee !== 'All') params.append('employee', filters.employee);
  if (filters.member && filters.member !== 'All') params.append('employee', filters.member);
  if (filters.working_days) params.append('working_days', filters.working_days);

  const queryString = params.toString();
  const url = `/api/planning/api/v1/teams-planning/export/${queryString ? `?${queryString}` : ''}`;

  const token = getAccessToken();
  if (!token) {
    throw new Error('Authentication required but no token found');
  }

  let fullUrl = `${getApiBaseUrl(true)}${url}`;
  fullUrl = ensureTenantQueryParam(fullUrl, getTenantSubdomain());

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), HEAVY_REQUEST_TIMEOUT_MS);

  try {
    const response = await fetch(fullUrl, {
      method: 'GET',
      headers: { Authorization: `Bearer ${token}` },
      signal: controller.signal,
    });
    clearTimeout(timeoutId);

    if (!response.ok) {
      const errorText = await response.text();
      let errorData;
      try {
        errorData = JSON.parse(errorText);
      } catch (e) {
        errorData = { detail: errorText || `HTTP ${response.status}: ${response.statusText}` };
      }
      throw {
        status: response.status,
        statusText: response.statusText,
        data: errorData,
      };
    }

    const disposition = response.headers.get('Content-Disposition') || '';
    const match = disposition.match(/filename="?([^"]+)"?/i);
    const filename =
      match?.[1] ||
      `teams-planning-events.${normalizedFormat === 'xlsx' ? 'xlsx' : 'csv'}`;

    const blob = await response.blob();
    return { blob, filename };
  } catch (error) {
    clearTimeout(timeoutId);
    if (error && error.status) throw error;
    if (isAbortError(error)) {
      throw { status: 408, data: { detail: 'Export request timed out. Please try again.' } };
    }
    throw error;
  }
}

/**
 * Get employee team operations stats
 * @param {object} filters - Filter options {date_from, date_to}
 * @returns {Promise} Team operations statistics with successful operations counts
 */
export async function getEmployeeTeamStats(filters = {}) {
  const params = new URLSearchParams();
  
  if (filters.date_from) params.append('date_from', filters.date_from);
  if (filters.date_to) params.append('date_to', filters.date_to);
  
  const queryString = params.toString();
  const url = `/api/accounts/api/v1/employee-dashboard/team-stats/${queryString ? `?${queryString}` : ''}`;
  
  try {
    return await apiCall(url, 'GET', null, true, { timeoutMs: HEAVY_REQUEST_TIMEOUT_MS });
  } catch (error) {
    // If endpoint doesn't exist yet, fallback to user's own imputations
    // This allows the frontend to work even without the backend endpoint
    console.warn('Employee team stats endpoint not available, using fallback');
    throw error;
  }
}

/**
 * Get team member operations (excluding current user) for chart grouping
 * @param {object} filters - Filter options {date_from, date_to}
 * @returns {Promise} Team member imputations {results, count, team_member_count}
 */
export async function getTeamOperationsByPeriod(filters = {}) {
  const params = new URLSearchParams();
  
  if (filters.date_from) params.append('date_from', filters.date_from);
  if (filters.date_to) params.append('date_to', filters.date_to);
  
  const queryString = params.toString();
  const url = `/api/accounts/api/v1/employee-dashboard/team-operations/${queryString ? `?${queryString}` : ''}`;
  
  return await apiCall(url, 'GET', null, true, { timeoutMs: HEAVY_REQUEST_TIMEOUT_MS });
}

/**
 * Get performance tracking statistics (badge and points)
 * @param {object} filters - Filter options {period, date_from, date_to, centre, team}
 * @returns {Promise} Performance statistics with badge distribution, trends, and top performers
 */
export async function getPerformanceTrackingStats(filters = {}) {
  const params = new URLSearchParams();
  
  if (filters.period) params.append('period', filters.period);
  if (filters.date_from) params.append('date_from', filters.date_from);
  if (filters.date_to) params.append('date_to', filters.date_to);
  if (filters.centre) params.append('centre', filters.centre);
  if (filters.team) params.append('team', filters.team);
  
  const queryString = params.toString();
  const url = `/api/accounts/api/v1/performance-tracking/stats/${queryString ? `?${queryString}` : ''}`;
  
  return await apiCall(url, 'GET', null, true, { timeoutMs: HEAVY_REQUEST_TIMEOUT_MS });
}

/**
 * Get manager/admin imputations table: paginated list with filters and filter options
 * @param {object} filters - Filter object {date_from, date_to, centre, team, employee, domain, operation, etat_final}
 * @param {number} page - Page number (default: 1)
 * @param {number} pageSize - Items per page (default: 20)
 * @returns {Promise<{ count, results, filter_options }>}
 */
export async function getManagerImputationsTable(filters = {}, page = 1, pageSize = 20) {
  const params = new URLSearchParams();
  if (filters.date_from) params.append('date_from', filters.date_from);
  if (filters.date_to) params.append('date_to', filters.date_to);
  if (filters.centre) params.append('centre', filters.centre);
  if (filters.team) params.append('team', filters.team);
  if (filters.employee) params.append('employee', filters.employee);
  if (filters.domain) params.append('domain', filters.domain);
  if (filters.operation) params.append('operation', filters.operation);
  if (filters.production_line_lp) params.append('production_line_lp', filters.production_line_lp);
  if (filters.project_type) params.append('project_type', filters.project_type);
  if (filters.program) params.append('program', filters.program);
  if (filters.etat_final) params.append('etat_final', filters.etat_final);
  if (Array.isArray(filters.weekdays) && filters.weekdays.length > 0) {
    params.append('weekdays', filters.weekdays.join(','));
  }
  params.append('page', String(page));
  params.append('page_size', String(pageSize));
  const queryString = params.toString();
  const url = `/api/accounts/api/v1/manager-dashboard/imputations-table/${queryString ? `?${queryString}` : ''}`;
  return await apiCall(url, 'GET', null, true, { timeoutMs: HEAVY_REQUEST_TIMEOUT_MS });
}

/**
 * Get manager dashboard statistics
 * @param {object} filters - Filter options {period, date_from, date_to, centre, team, employee, domain}
 * @returns {Promise} Dashboard statistics with KPIs, charts data, and tables
 */
export async function getManagerDashboardStats(filters = {}) {
  const params = new URLSearchParams();
  
  // Only send period if it's not "Custom Range" (custom range uses date_from/date_to instead)
  if (filters.period && filters.period !== 'Custom Range') {
    params.append('period', filters.period);
  }
  if (filters.date_from) params.append('date_from', filters.date_from);
  if (filters.date_to) params.append('date_to', filters.date_to);
  if (filters.centre) params.append('centre', filters.centre);
  if (filters.team) params.append('team', filters.team);
  if (filters.employee) params.append('employee', filters.employee);
  if (Array.isArray(filters.domain) && filters.domain.length > 0) {
    filters.domain.forEach((d) => params.append('domain', d));
  } else if (filters.domain) {
    params.append('domain', filters.domain);
  }
  if (filters.operation) params.append('operation', filters.operation);
  if (filters.production_line_lp) params.append('production_line_lp', filters.production_line_lp);
  if (filters.project_type) params.append('project_type', filters.project_type);
  if (filters.program) params.append('program', filters.program);
  if (filters.includeIncompleteOperations) params.append('include_incomplete', 'true');
  if (filters.showAllStatuses) params.append('show_all_statuses', 'true');
  if (Array.isArray(filters.weekdays) && filters.weekdays.length > 0) {
    params.append('weekdays', filters.weekdays.join(','));
  }
  
  const queryString = params.toString();
  const url = `/api/accounts/api/v1/manager-dashboard/stats/${queryString ? `?${queryString}` : ''}`;

  return await apiCall(url, 'GET', null, true, { timeoutMs: HEAVY_REQUEST_TIMEOUT_MS });
}

/**
 * Export manager dashboard data as CSV or Excel
 * @param {object} filters - Filter options {period, date_from, date_to, centre, team, employee, domain}
 * @param {string} format - Export format: 'csv' or 'excel'
 * @returns {Promise<Blob>} Blob containing the exported file
 */
export async function exportManagerDashboardData(filters = {}, format = 'csv') {
  // Normalize format (handle 'xlsx' as 'excel')
  const normalizedFormat = (format === 'excel' || format === 'xlsx') ? 'excel' : 'csv';
  
  const params = new URLSearchParams();
  
  // Only send period if it's not "Custom Range" (custom range uses date_from/date_to instead)
  if (filters.period && filters.period !== 'Custom Range') params.append('period', filters.period);
  if (filters.date_from) params.append('date_from', filters.date_from);
  if (filters.date_to) params.append('date_to', filters.date_to);
  if (filters.centre) params.append('centre', filters.centre);
  if (filters.team) params.append('team', filters.team);
  if (filters.employee) params.append('employee', filters.employee);
  if (Array.isArray(filters.domain) && filters.domain.length > 0) {
    filters.domain.forEach((d) => params.append('domain', d));
  } else if (filters.domain) {
    params.append('domain', filters.domain);
  }
  if (filters.operation) params.append('operation', filters.operation);
  if (filters.production_line_lp) params.append('production_line_lp', filters.production_line_lp);
  if (filters.project_type) params.append('project_type', filters.project_type);
  if (filters.program) params.append('program', filters.program);
  if (Array.isArray(filters.weekdays) && filters.weekdays.length > 0) {
    params.append('weekdays', filters.weekdays.join(','));
  }
  
  const queryString = params.toString();
  // Use format-specific endpoint
  const baseUrl = `/api/accounts/api/v1/manager-dashboard/export/${normalizedFormat}/`;
  const url = queryString ? `${baseUrl}?${queryString}` : baseUrl;
  
  // Get token for authentication
  const token = getAccessToken();
  if (!token) {
    throw new Error('Authentication required but no token found');
  }
  
  let fullUrl = `${getApiBaseUrl(true)}${url}`;
  fullUrl = ensureTenantQueryParam(fullUrl, getTenantSubdomain());

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), HEAVY_REQUEST_TIMEOUT_MS);
  let responseReceived = false;

  try {
    const response = await fetch(fullUrl, {
      method: 'GET',
      headers: { 'Authorization': `Bearer ${token}` },
      signal: controller.signal,
    });
    responseReceived = true;
    clearTimeout(timeoutId);
  
  if (!response.ok) {
    const errorText = await response.text();
    let errorData;
    try {
      errorData = JSON.parse(errorText);
    } catch (e) {
      errorData = { detail: errorText || `HTTP ${response.status}: ${response.statusText}` };
    }
    throw {
      status: response.status,
      statusText: response.statusText,
      data: errorData,
    };
  }
  
  // Return blob for file download
  const blob = await response.blob();
  return blob;
  } catch (error) {
    clearTimeout(timeoutId);
    if (error && error.status) throw error;
    if (isAbortError(error)) {
      throw { status: 0, code: 'TIMEOUT', statusText: 'Request Timeout', data: { error: [TIMEOUT_ERROR_MESSAGE] } };
    }
    if (responseReceived) {
      throw { status: 0, code: 'BODY_READ_ERROR', statusText: 'Connection Lost', data: { error: [BODY_READ_ERROR_MESSAGE] } };
    }
    throw { status: 0, code: 'NETWORK', statusText: 'Network Error', data: { error: [CONNECTION_ERROR_MESSAGE] } };
  }
}

/**
 * Get available rewards catalog
 * @param {object} filters - Optional filters {category, availability}
 * @returns {Promise} Rewards list with requires_approval field
 */
export async function getRewards(filters = {}) {
  const params = new URLSearchParams();
  if (filters.category) params.append('category', filters.category);
  if (filters.is_active !== undefined) params.append('is_active', filters.is_active);
  if (filters.is_global !== undefined) params.append('is_global', filters.is_global);
  
  const queryString = params.toString();
  const url = `/api/accounts/api/v1/rewards/${queryString ? `?${queryString}` : ''}`;
  
  try {
    const response = await apiCall(url, 'GET', null, true);
    // Handle both array and paginated response
    if (Array.isArray(response)) {
      return { results: response };
    }
    return response;
  } catch (error) {
    // If endpoint doesn't exist yet, return empty results
    // Component will fall back to mock data
    console.warn('Rewards endpoint not available, using fallback');
    return { results: [] };
  }
}

/**
 * Redeem a reward
 * @param {number} rewardId - Reward ID to redeem
 * @param {string} notes - Optional notes for the redemption request
 * @returns {Promise} Redemption response with redemption_id and status
 */
export async function redeemReward(rewardId, notes = '') {
  const url = `/api/accounts/api/v1/rewards/${rewardId}/redeem/`;
  
  try {
    return await apiCall(url, 'POST', { notes: notes || '' }, true);
  } catch (error) {
    // If endpoint doesn't exist yet, return mock success response
    // Component will show placeholder message
    console.warn('Redeem reward endpoint not available, using fallback');
    return {
      id: Date.now(), // Mock redemption ID
      status: 'pending',
      message: 'Redemption request submitted. Awaiting manager approval. (Placeholder - backend pending)',
      reward_id: rewardId
    };
  }
}

/**
 * Get redemption history for current user
 * @param {object} filters - Optional filters {status, page, page_size}
 * @returns {Promise} Paginated redemption history
 */
export async function getRedemptionHistory(filters = {}) {
  const params = new URLSearchParams();
  if (filters.status) params.append('status', filters.status);
  if (filters.reward) params.append('reward', filters.reward);
  if (filters.employee) params.append('employee', filters.employee);
  if (filters.page) params.append('page', filters.page);
  if (filters.page_size) params.append('page_size', filters.page_size);
  
  const queryString = params.toString();
  const url = `/api/accounts/api/v1/rewards/redemptions/${queryString ? `?${queryString}` : ''}`;
  
  try {
    const response = await apiCall(url, 'GET', null, true);
    // Handle both array and paginated response
    if (Array.isArray(response)) {
      return { results: response };
    }
    return response;
  } catch (error) {
    // If endpoint doesn't exist yet, return empty results
    console.warn('Redemption history endpoint not available, using fallback');
    return { count: 0, results: [] };
  }
}

/**
 * Get single reward
 * @param {number} rewardId - Reward ID
 * @returns {Promise} Reward data
 */
export async function getReward(rewardId) {
  return await apiCall(`/api/accounts/api/v1/rewards/${rewardId}/`, 'GET', null, true);
}

/**
 * Create reward (managers and admins only)
 * @param {object} rewardData - Reward data
 * @returns {Promise} Created reward
 */
export async function createReward(rewardData) {
  return await apiCall('/api/accounts/api/v1/rewards/', 'POST', rewardData, true);
}

/**
 * Update reward (managers and admins only)
 * @param {number} rewardId - Reward ID
 * @param {object} rewardData - Reward data to update
 * @returns {Promise} Updated reward
 */
export async function updateReward(rewardId, rewardData) {
  return await apiCall(`/api/accounts/api/v1/rewards/${rewardId}/`, 'PATCH', rewardData, true);
}

/**
 * Delete reward (managers and admins only)
 * @param {number} rewardId - Reward ID
 * @returns {Promise} Response
 */
export async function deleteReward(rewardId) {
  return await apiCall(`/api/accounts/api/v1/rewards/${rewardId}/`, 'DELETE', null, true);
}

/**
 * Approve redemption (managers and admins only)
 * @param {number} redemptionId - Redemption ID
 * @param {string} notes - Optional manager notes
 * @returns {Promise} Updated redemption
 */
export async function approveRedemption(redemptionId, notes = '') {
  return await apiCall(`/api/accounts/api/v1/rewards/redemptions/${redemptionId}/`, 'PATCH', {
    status: 'approved',
    manager_notes: notes
  }, true);
}

/**
 * Reject redemption (managers and admins only)
 * @param {number} redemptionId - Redemption ID
 * @param {string} reason - Rejection reason
 * @param {string} notes - Optional manager notes
 * @returns {Promise} Updated redemption
 */
export async function rejectRedemption(redemptionId, reason, notes = '') {
  return await apiCall(`/api/accounts/api/v1/rewards/redemptions/${redemptionId}/`, 'PATCH', {
    status: 'rejected',
    rejection_reason: reason,
    manager_notes: notes
  }, true);
}

// Export default object with all functions
const api = {
  login,
  verifyMfa,
  getMfaStatus,
  enableTotpSetup,
  confirmTotp,
  enableEmailMfa,
  disableMfa,
  disableMfaForUser,
  register,
  forgotPassword,
  changePassword,
  resetPasswordConfirm,
  getAccessToken,
  getRefreshToken,
  setTokens,
  clearTokens,
  isAuthenticated,
  setMustChangePassword,
  getMustChangePassword,
  clearMustChangePassword,
  getUsers,
  getUser,
  createUser,
  updateUser,
  deleteUser,
  getCurrentUserProfile,
  updateCurrentUserProfile,
  getTeams,
  getTeam,
  getTeamsByCentre,
  getTeamsFilter,
  updateTeam,
  createTeam,
  deleteTeam,
  getCentres,
  getCentre,
  getCentresFilter,
  createCentre,
  updateCentre,
  deleteCentre,
  getOperations,
  getActiveOperationsJson,
  getOperation,
  createOperation,
  updateOperation,
  toggleOperationStatus,
  getOperationDeleteInfo,
  deleteOperation,
  getOperationHistory,
  getJiraTickets,
  getJiraTicketsStats,
  patchJiraTicket,
  insertJiraImputation,
  getJiraComments,
  getJiraCommentsStats,
  getJiraCommentEmployees,
  createJiraComment,
  detectJiraCommentOperation,
  insertJiraCommentImputation,
  getImputations,
  getImputation,
  checkImputationDuplicate,
  createImputation,
  updateImputation,
  deleteImputation,
  getEvents,
  createEvent,
  updateEvent,
  deleteEvent,
  getOutlookStatus,
  getOutlookConnectUrl,
  disconnectOutlook,
  previewOutlookWeek,
  importOutlookEvents,
  getCategories,
  createCategory,
  updateCategory,
  deleteCategory,
  getReminderSchedule,
  updateReminderSchedule,
  getTeamsPlanningStats,
  getTeamsPlanningPending,
  sendTeamsPlanningReminders,
  exportTeamsPlanningEvents,
  getManagerDashboardStats,
  getBadgeAwards,
  getBadgeConfigurations,
  getBadgeConfiguration,
  updateBadgeConfiguration,
  createBadgeConfiguration,
  getBadgeConfigurationHistory,
  testTriggerBadgeAward,
  getBadgeIconUrl,
  getNotifications,
  markNotificationRead,
  markAllNotificationsRead,
  getRewards,
  redeemReward,
  getRedemptionHistory,
  formatApiError,
  apiCall,
};

export default api;

